/**
 * Vue Liste : tableau dense, tri par colonne (dont le lot), filtres, pagination (`meta`),
 * sélection multiple avec actions groupées.
 */

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LIMITS, TASK_PRIORITIES, TASK_STATUSES } from '@exiplan/shared-types';
import { buildRows } from '@exiplan/gantt';
import { api } from '../lib/api.js';
import { compareByLot, lotOf } from '../lib/lots.js';
import { graftOrphans } from '../lib/tree.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert, appConfirm } from '../dialogs/service.js';
import {
  Empty,
  LoadError,
  PriorityTag,
  StatusTag,
  labelForPriority,
  labelForStatus,
  taskReference,
} from '../components/common.jsx';
import { BTN_GHOST_SM, CHECK, CHOIX_LABEL, INPUT_SM, SELECT_SM, TD, TH } from '../ui/theme.js';

/**
 * Chevron de repli, compact pour une cellule de tableau dense. `BTN_ICON` (barre d'outils) porte
 * `p-2` plus une bordure transparente de 2 px — environ 30 px de large, contre 16 px pour le vide
 * `w-4` posé sur une feuille : le titre d'un enfant se retrouvait décalé de ~14 px vers la
 * gauche par rapport à son parent, ce qui annulait presque toute l'indentation. Le chevron et le
 * vide qui le remplace sont maintenant posés dans le même conteneur de largeur fixe
 * (`inline-flex w-5 shrink-0 justify-center`), pour que seule l'indentation les distingue.
 */
const TREE_TOGGLE = 'px-0.5 text-ink-soft hover:text-ink';

const SORTABLE_KEYS = [
  { key: 'reference', labelKey: 'listView.columns.reference' },
  { key: 'title', labelKey: 'listView.columns.title' },
  { key: 'lot', labelKey: 'listView.columns.lot' },
  { key: 'status', labelKey: 'listView.columns.status' },
  { key: 'priority', labelKey: 'listView.columns.priority' },
  { key: 'dueDate', labelKey: 'listView.columns.dueDate' },
];

export function ListView({ projectId, projectKey, onOpenTask, reloadKey }) {
  const { t } = useTranslation();
  const SORTABLE = SORTABLE_KEYS.map((col) => ({ key: col.key, label: t(col.labelKey) }));
  const [filters, setFilters] = useState({ q: '', status: '', priority: '' });
  const [sort, setSort] = useState({ key: 'reference', dir: 'asc' });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(() => new Set());
  const [hierarchy, setHierarchy] = useState(true);
  const [collapsedIds, setCollapsedIds] = useState(() => new Set());

  const query = new URLSearchParams();
  if (filters.q) query.set('q', filters.q);
  if (filters.status) query.set('status', filters.status);
  if (filters.priority) query.set('priority', filters.priority);
  query.set('page', String(page));
  query.set('pageSize', '25');

  const {
    data: response,
    error,
    loading,
    reload,
  } = useAsync(
    (signal) => api.get(`/projects/${projectId}/tasks?${query.toString()}`, { signal }),
    [projectId, filters.q, filters.status, filters.priority, page, reloadKey],
  );

  /**
   * Toutes les phases du projet, indépendamment de la page et des filtres : sans elles, le lot
   * d'une tâche dont la phase est restée sur une autre page serait inconnu (§ `lib/lots.js`).
   */
  const { data: phasesResponse } = useAsync(
    (signal) =>
      api.get(`/projects/${projectId}/tasks?kind=phase&pageSize=${LIMITS.maxPageSize}`, {
        signal,
      }),
    [projectId, reloadKey],
  );

  const tasks = response?.data ?? [];
  const meta = response?.meta ?? { total: 0, page: 1, pageSize: 25, pageCount: 1 };

  const resolveLot = useMemo(() => {
    const byId = new Map((phasesResponse?.data ?? []).map((phase) => [phase.id, phase]));
    for (const task of tasks) byId.set(task.id, task);
    // Relu par identifiant : en hiérarchie, `graftOrphans` rend des copies dont le `parentId`
    // hors page est effacé — précisément le lien dont le lot a besoin.
    return (task) => lotOf(byId.get(task.id) ?? task, byId);
  }, [phasesResponse, tasks]);

  const sorted = useMemo(() => {
    const list = [...tasks];
    if (sort.key === 'lot') return list.sort(compareByLot(sort.dir, resolveLot));
    list.sort((a, b) => {
      const av = sort.key === 'reference' ? a.number : a[sort.key];
      const bv = sort.key === 'reference' ? b.number : b[sort.key];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [tasks, sort, resolveLot]);

  /**
   * Lignes à peindre. En mode hiérarchie, l'ordre vient de `buildRows` de `@exiplan/gantt` —
   * la fonction qui ordonne déjà le Gantt, pas une seconde implémentation de l'arbre.
   *
   * `graftOrphans` la précède parce que la liste est **paginée et filtrable** : sans elle, une
   * tâche dont le lot est resté sur une autre page ne serait atteignable depuis aucune racine
   * et disparaîtrait de l'écran sans un mot.
   *
   * `buildRows` conserve l'ordre d'entrée à l'intérieur de chaque fratrie : lui passer la liste
   * déjà triée suffit à trier les sœurs entre elles, sans toucher à l'arbre.
   */
  const rows = useMemo(() => {
    if (!hierarchy) {
      return sorted.map((task) => ({ task, depth: 0, hasChildren: false, visible: true }));
    }
    return buildRows(graftOrphans(sorted), [...collapsedIds]);
  }, [hierarchy, sorted, collapsedIds]);

  const visibleRows = useMemo(() => rows.filter((row) => row.visible), [rows]);

  const toggleCollapsed = (id) =>
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onSort = (key) =>
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc',
    }));

  const updateFilter = useCallback((patch) => {
    setPage(1);
    setFilters((current) => ({ ...current, ...patch }));
  }, []);

  const toggleSelect = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelected((current) =>
      current.size === visibleRows.length
        ? new Set()
        : new Set(visibleRows.map((row) => row.task.id)),
    );

  /**
   * `selected` conserve les identifiants cochés même quand un repli les masque ensuite : une
   * action groupée ne doit porter que sur ce que l'écran montre réellement, sous peine
   * d'emporter des tâches invisibles (cf. `toggleSelectAll`, qui se compare déjà à
   * `visibleRows`).
   */
  const selectedVisibleIds = useMemo(() => {
    const visibleIds = new Set(visibleRows.map((row) => row.task.id));
    return [...selected].filter((id) => visibleIds.has(id));
  }, [selected, visibleRows]);

  async function bulkStatus(status) {
    try {
      await Promise.all(
        selectedVisibleIds.map((id) => api.patch(`/projects/${projectId}/tasks/${id}`, { status })),
      );
      setSelected(new Set());
      reload();
    } catch (error) {
      await appAlert(t('listView.statusChangeFailed', { message: error.message }));
    }
  }

  async function bulkDelete() {
    if (!(await appConfirm(t('listView.deleteConfirm', { count: selectedVisibleIds.length }))))
      return;
    try {
      await Promise.all(
        selectedVisibleIds.map((id) => api.delete(`/projects/${projectId}/tasks/${id}`)),
      );
      setSelected(new Set());
      reload();
    } catch (error) {
      await appAlert(t('listView.deleteFailed', { message: error.message }));
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          className={`${INPUT_SM} w-56`}
          placeholder={t('listView.searchPlaceholder')}
          aria-label={t('listView.searchAria')}
          value={filters.q}
          onChange={(e) => updateFilter({ q: e.target.value })}
        />
        <select
          className={SELECT_SM}
          aria-label={t('listView.filterStatusAria')}
          value={filters.status}
          onChange={(e) => updateFilter({ status: e.target.value })}
        >
          <option value="">{t('listView.allStatuses')}</option>
          {TASK_STATUSES.map((s) => (
            <option key={s} value={s}>
              {labelForStatus(s, t)}
            </option>
          ))}
        </select>
        <select
          className={SELECT_SM}
          aria-label={t('listView.filterPriorityAria')}
          value={filters.priority}
          onChange={(e) => updateFilter({ priority: e.target.value })}
        >
          <option value="">{t('listView.allPriorities')}</option>
          {TASK_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {labelForPriority(p, t)}
            </option>
          ))}
        </select>
        <label className={CHOIX_LABEL}>
          <input
            className={CHECK}
            type="checkbox"
            checked={hierarchy}
            onChange={(e) => setHierarchy(e.target.checked)}
          />
          {t('listView.hierarchy')}
        </label>

        {selected.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-ink-faint">
              {t('listView.selectedCount', { count: selected.size })}
            </span>
            <select
              className={SELECT_SM}
              defaultValue=""
              onChange={(e) => e.target.value && bulkStatus(e.target.value)}
              aria-label={t('listView.changeStatusAria')}
            >
              <option value="">{t('listView.changeStatusPlaceholder')}</option>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {labelForStatus(s, t)}
                </option>
              ))}
            </select>
            <button type="button" className={BTN_GHOST_SM} onClick={bulkDelete}>
              {t('listView.delete')}
            </button>
          </div>
        )}
      </div>

      {/* `loading` garde l'état vide : sans lui, `tasks` vaut `[]` le temps de la réponse et la
          vue affichait la cellule hachurée « aucune tâche » à un projet qui en a — un état vide
          n'est une vérité qu'une fois la réponse arrivée. Même garde que ProjectsPage et
          TeamsPage ; le défaut y avait été constaté à l'écran, pas par les tests. */}
      {loading ? (
        <p className="p-4 text-sm text-ink-soft">{t('listView.loading')}</p>
      ) : error ? (
        <LoadError error={error} onRetry={reload} className="m-4" />
      ) : visibleRows.length === 0 ? (
        <Empty>{t('listView.empty')}</Empty>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className={TH}>
                <input
                  className={CHECK}
                  type="checkbox"
                  aria-label={t('listView.selectAllAria')}
                  checked={selected.size === visibleRows.length}
                  onChange={toggleSelectAll}
                />
              </th>
              {SORTABLE.map((col) => (
                <th key={col.key} className={TH}>
                  <button type="button" className="uppercase" onClick={() => onSort(col.key)}>
                    {col.label} {sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : ''}
                  </button>
                </th>
              ))}
              <th className={TH}>{t('listView.assignee')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map(({ task, depth, hasChildren }) => (
              <tr key={task.id} className="hover:bg-line-soft">
                <td className={TD}>
                  <input
                    className={CHECK}
                    type="checkbox"
                    aria-label={t('listView.selectRowAria', { title: task.title })}
                    checked={selected.has(task.id)}
                    onChange={() => toggleSelect(task.id)}
                  />
                </td>
                <td className={TD}>
                  <button
                    type="button"
                    className="font-mono text-[11px] text-ink-faint"
                    onClick={() => onOpenTask(task.id)}
                  >
                    {taskReference(projectKey ?? '…', task.number)}
                  </button>
                </td>
                <td className={TD}>
                  {/* L'indentation dépend de la profondeur de la tâche dans l'arbre,
                      inconnue à la compilation. Une échelle de classes figées plafonnerait
                      l'arbre à la profondeur la plus grande prévue, que `LIMITS.maxTaskDepth`
                      peut faire évoluer. */}
                  <div
                    className="flex items-center gap-1.5"
                    // exiui:allow style-inline — géométrie calculée
                    style={{ paddingLeft: depth > 0 ? `${depth * 1.5}rem` : undefined }}
                  >
                    <span className="inline-flex w-5 shrink-0 justify-center">
                      {hasChildren ? (
                        <button
                          type="button"
                          className={TREE_TOGGLE}
                          aria-label={
                            collapsedIds.has(task.id)
                              ? t('listView.expandAria', { title: task.title })
                              : t('listView.collapseAria', { title: task.title })
                          }
                          onClick={() => toggleCollapsed(task.id)}
                        >
                          {collapsedIds.has(task.id) ? '▸' : '▾'}
                        </button>
                      ) : (
                        <span aria-hidden="true" />
                      )}
                    </span>
                    <button type="button" onClick={() => onOpenTask(task.id)}>
                      {task.title}
                    </button>
                  </div>
                </td>
                <td className={TD}>{resolveLot(task)?.title ?? '—'}</td>
                <td className={TD}>
                  <StatusTag status={task.status} />
                </td>
                <td className={TD}>
                  <PriorityTag priority={task.priority} />
                </td>
                <td className={TD}>{task.dueDate ?? '—'}</td>
                <td className={TD}>{task.assignee?.name ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {meta.pageCount > 1 && (
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] text-ink-faint">
            {t('listView.pageInfo', {
              page: meta.page,
              pageCount: meta.pageCount,
              total: meta.total,
            })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className={BTN_GHOST_SM}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t('common.previous')}
            </button>
            <button
              type="button"
              className={BTN_GHOST_SM}
              disabled={page >= meta.pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              {t('common.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
